import { useQuery } from "@tanstack/react-query";
import { fetchUserVotes, type UserVotesWithPercentages } from "@/services";
import { QUERY_KEYS, QUERY_CONFIG } from "@/config";
import { getNextEpoch } from "@/utils/epoch";
import { useAccount } from "wagmi";

export interface UserVotesStore {
    nextEpochVotes: UserVotesWithPercentages | null;
    currentEpochVotes: UserVotesWithPercentages | null;
    isLoading: boolean;
    error: Error | null;
}

export const useUserVotesStore = (): UserVotesStore => {
    const { address } = useAccount();
    const nextEpoch = getNextEpoch();
    const currentEpoch = nextEpoch - 1;

    const {
        data: nextEpochVotes,
        isLoading: isCurrentLoading,
        error: currentError,
    } = useQuery<UserVotesWithPercentages>({
        queryKey: QUERY_KEYS.VOTES.USER(nextEpoch, address),
        queryFn: () => fetchUserVotes(nextEpoch, address!),
        enabled: !!address,
        ...QUERY_CONFIG.VOTES,
    });

    const {
        data: currentEpochVotes,
        isLoading: isPreviousLoading,
        error: previousError,
    } = useQuery<UserVotesWithPercentages>({
        queryKey: QUERY_KEYS.VOTES.USER(currentEpoch, address),
        queryFn: () => fetchUserVotes(currentEpoch, address!),
        enabled: !!address,
        ...QUERY_CONFIG.VOTES,
    });

    return {
        nextEpochVotes: nextEpochVotes ?? null,
        currentEpochVotes: currentEpochVotes ?? null,
        isLoading: isCurrentLoading || isPreviousLoading,
        error: (currentError || previousError) as Error | null,
    };
};
